import { ICorporationKcfInformationResponse } from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { TextInlineEditor } from 'components/inline_edit/TextInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { validateNetSuiteId } from 'pages/Settings/shared/shared';
import { updateCorporationKcfInformation } from 'utils/corporation';

export type CorporationNetSuiteIdInlineEditorProps = Readonly<{
    /**
     * The corporation.
     */
    corporationId: string;
    /**
     * The KCF-only information associated with the corporation
     */
    corporationKcfInfo: ICorporationKcfInformationResponse;
    /**
     * The callback for when data changes.
     */
    onChange: () => void;
}>;

export function CorporationNetSuiteIdInlineEditor({
    corporationId,
    corporationKcfInfo,
    onChange,
}: CorporationNetSuiteIdInlineEditorProps) {
    const { canViewKcfInfo, canEditKcfInfo } = usePermissions();
    const { showToast } = useNotificationContext();
    const { mutate: updateNetSuiteId, isPending } = useMutation({
        mutationFn: (netSuiteId: string | undefined) =>
            updateCorporationKcfInformation(corporationId, corporationKcfInfo, {
                NetSuiteId: netSuiteId || undefined,
            }),

        onSuccess: () => {
            onChange();
            showToast({
                type: 'success',
                content: 'Corporation NetSuite ID saved',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to save corporation NetSuite ID',
            }),
    });

    if (!canViewKcfInfo) {
        return <></>;
    }

    return (
        <TextInlineEditor
            value={corporationKcfInfo.NetSuiteId}
            isDisabled={isPending || !canEditKcfInfo}
            onConfirm={(netSuiteId) => updateNetSuiteId(netSuiteId)}
            validate={(netSuiteId) => {
                if (netSuiteId) {
                    return validateNetSuiteId(netSuiteId);
                }
                return undefined;
            }}
            renderValue={(netSuiteId) => <code>{netSuiteId}</code>}
        />
    );
}
